"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import { ItemCategory } from "@/lib/types";
import { detectCategoryFromTitle } from "@/lib/categoryDetectionService";

interface CategoryAutoDetectorProps {
  /** The item title to analyze for category detection */
  title: string;
  /** Callback function called when category/subcategory is detected */
  onDetect: (category: ItemCategory | "", subcategory: string) => void;
  /** Minimum number of words required before detection triggers (default: 3) */
  minWords?: number;
  /** Debounce delay in milliseconds (default: 1000) */
  debounceMs?: number;
  /** Show loading indicator when detecting (default: false) */
  showLoadingIndicator?: boolean;
  /** Custom className for the loading indicator */
  className?: string;
  /** Whether to enable client-side caching (default: true) */
  enableCache?: boolean;
  /** Optional callback to notify parent when detection state changes */
  onDetectingChange?: (isDetecting: boolean) => void;
}

interface DetectionResult {
  category: ItemCategory | "";
  type: string;
}

// Client-side cache with size limit
const detectionCache = new Map<string, DetectionResult>();
const MAX_CACHE_SIZE = 100;

/**
 * CategoryAutoDetector Component
 *
 * Automatically detects category and subcategory from item title.
 * Uses client-side word-based matching algorithm matching Insights Platform approach.
 *
 * Algorithm:
 * - Splits title into words (minimum 3 characters, alphanumeric only)
 * - Scores each market item by matching word count
 * - Awards +2 bonus for exact phrase matches
 * - Selects the highest-scoring match
 *
 * @example
 * ```tsx
 * <CategoryAutoDetector
 * title={formData.title}
 * onDetect={(category, subcategory) => {
 * setFormData(prev => ({
 * ...prev,
 * category,
 * subcategory
 * }));
 * }}
 * minChars={5}
 * showLoadingIndicator={true}
 * />
 * ```
 */
export function CategoryAutoDetector({
  title,
  onDetect,
  minWords = 3,
  debounceMs = 1000,
  showLoadingIndicator = false,
  className = "",
  enableCache = true,
  onDetectingChange,
}: CategoryAutoDetectorProps) {
  const [isDetecting, setIsDetecting] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const onDetectingChangeRef = useRef(onDetectingChange);

  // Track the last detected values to avoid unnecessary callbacks
  const lastDetectedRef = useRef<{
    category: ItemCategory | "";
    type: string;
  } | null>(null);
  const hasInitializedRef = useRef(false);

  // Update ref when callback changes
  useEffect(() => {
    onDetectingChangeRef.current = onDetectingChange;
  }, [onDetectingChange]);

  // Helper function to count words
  const countWords = useCallback((text: string): number => {
    return text
      .trim()
      .split(/\s+/)
      .filter((word) => word.length > 0).length;
  }, []);

  // Memoize the callback to prevent unnecessary re-renders
  // Use ref to store the latest onDetect callback to avoid dependency issues
  const onDetectRef = useRef(onDetect);

  // Update ref when callback changes
  useEffect(() => {
    onDetectRef.current = onDetect;
  }, [onDetect]);

  const handleDetect = useCallback(
    (category: ItemCategory | "", subcategory: string, force = false) => {
      // Only call callback if values actually changed or if forced
      const lastDetected = lastDetectedRef.current;
      if (
        force ||
        !lastDetected ||
        lastDetected.category !== category ||
        lastDetected.type !== subcategory
      ) {
        lastDetectedRef.current = { category, type: subcategory };
        onDetectRef.current(category, subcategory);
      }
    },
    [], // Empty deps - we use ref to get latest callback
  );

  useEffect(() => {
    const trimmedTitle = title.trim();
    const wordCount = countWords(trimmedTitle);

    // Cancel any previous request immediately when title changes
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    // Reset if title is cleared - only call callback if we had previous detection
    if (!trimmedTitle) {
      // Only clear if we had something detected before
      if (
        lastDetectedRef.current &&
        (lastDetectedRef.current.category || lastDetectedRef.current.type)
      ) {
        handleDetect("", "", true);
      }
      const timer = setTimeout(() => {
        setIsDetecting(false);
        onDetectingChangeRef.current?.(false);
      }, 0);
      hasInitializedRef.current = true;
      return () => clearTimeout(timer);
    }

    // Wait for minimum words - only reset if we had previous detection
    if (wordCount < minWords) {
      // Only clear if we had something detected before
      if (
        lastDetectedRef.current &&
        (lastDetectedRef.current.category || lastDetectedRef.current.type)
      ) {
        handleDetect("", "", true);
      }
      const timer = setTimeout(() => {
        setIsDetecting(false);
        onDetectingChangeRef.current?.(false);
      }, 0);
      hasInitializedRef.current = true;
      return () => clearTimeout(timer);
    }

    // Check client-side cache first (if enabled)
    // Only use cache if title exactly matches (case-insensitive)
    if (enableCache) {
      const cacheKey = trimmedTitle.toLowerCase();
      if (detectionCache.has(cacheKey)) {
        const cached = detectionCache.get(cacheKey)!;
        // Use cached result immediately
        handleDetect(cached.category, cached.type);
        const timer = setTimeout(() => {
          setIsDetecting(false);
          onDetectingChangeRef.current?.(false);
        }, 0);
        return () => clearTimeout(timer);
      }
    }

    // Create new abort controller for this detection
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    const startTimer = setTimeout(() => {
      setIsDetecting(true);
      onDetectingChangeRef.current?.(true);
    }, 0);

    // Store the title at the time of scheduling the detection
    const scheduledTitle = trimmedTitle;

    const timeoutId = setTimeout(async () => {
      try {
        // Re-validate title before making request
        // Get fresh title value to ensure it hasn't changed
        const currentTitle = title.trim();

        // Verify title hasn't changed since we scheduled this detection
        if (currentTitle !== scheduledTitle) {
          // Title changed, abort this detection
          setIsDetecting(false);
          onDetectingChangeRef.current?.(false);
          return;
        }

        const currentWordCount = countWords(currentTitle);
        if (!currentTitle || currentWordCount < minWords) {
          setIsDetecting(false);
          onDetectingChangeRef.current?.(false);
          return;
        }

        // Double-check abort status before making request
        if (abortController.signal.aborted) {
          setIsDetecting(false);
          onDetectingChangeRef.current?.(false);
          return;
        }

        // Use client-side word-based detection (matching Insights Platform approach)
        const result: DetectionResult =
          await detectCategoryFromTitle(currentTitle);

        // Final check: verify title hasn't changed and request wasn't aborted
        const finalTitle = title.trim();
        if (
          abortController.signal.aborted ||
          finalTitle !== scheduledTitle ||
          finalTitle !== currentTitle
        ) {
          // Title changed or request was aborted, discard result
          setIsDetecting(false);
          onDetectingChangeRef.current?.(false);
          return;
        }

        // Verify word count is still valid
        const finalWordCount = countWords(finalTitle);
        if (finalWordCount < minWords) {
          setIsDetecting(false);
          onDetectingChangeRef.current?.(false);
          return;
        }

        // All checks passed - update via callback
        console.log("CategoryAutoDetector: Detection successful", {
          category: result.category,
          type: result.type,
          title: currentTitle,
          hasCategory: !!result.category,
          hasType: !!result.type,
        });

        // Cache result (if enabled) before calling callback
        if (enableCache) {
          const cacheKeyForResult = currentTitle.toLowerCase();
          if (detectionCache.size >= MAX_CACHE_SIZE) {
            // Remove oldest entry (FIFO)
            const firstKey = detectionCache.keys().next().value;
            if (firstKey) {
              detectionCache.delete(firstKey);
            }
          }
          detectionCache.set(cacheKeyForResult, result);
        }

        // Always call callback if we got results, or if we need to clear previous results
        const hasResults = result.category || result.type;
        const hadPreviousResults =
          lastDetectedRef.current &&
          (lastDetectedRef.current.category || lastDetectedRef.current.type);

        console.log("CategoryAutoDetector: Preparing to call callback", {
          hasResults,
          hadPreviousResults,
          result,
          lastDetected: lastDetectedRef.current,
        });

        if (hasResults || hadPreviousResults) {
          // Call the detection callback
          console.log("CategoryAutoDetector: Calling handleDetect", {
            category: result.category,
            type: result.type,
          });
          handleDetect(result.category, result.type, true);
        } else if (!hasInitializedRef.current) {
          // On first initialization with no results, mark as initialized but don't call callback
          console.log(
            "CategoryAutoDetector: No results, marking as initialized",
          );
          hasInitializedRef.current = true;
        }

        // Update detecting state after calling callback
        if (!abortController.signal.aborted) {
          setIsDetecting(false);
          onDetectingChangeRef.current?.(false);
        }
        hasInitializedRef.current = true;
      } catch (error: unknown) {
        // Ignore abort errors - they're expected when title changes
        if (error instanceof Error && error.name === "AbortError") {
          return;
        }
        console.error("Category detection error:", error);
        // Don't call onDetect on error - let user manually select
        // But still update detecting state
        setIsDetecting(false);
        onDetectingChangeRef.current?.(false);
      }
    }, debounceMs);

    return () => {
      clearTimeout(startTimer);
      clearTimeout(timeoutId);
      // Abort the request if component unmounts or title changes
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, [
    title, // Main dependency - triggers re-detection on any title change
    handleDetect, // Stable callback (no deps)
    minWords,
    debounceMs,
    enableCache,
    countWords, // Stable callback
  ]);

  if (showLoadingIndicator && isDetecting) {
    return (
      <div className={`text-xs text-text-light ${className}`}>
        <span className="inline-block animate-spin mr-1">⏳</span>
        Detecting category and subcategory...
      </div>
    );
  }

  return null;
}
